import { useCallback, useRef, useState } from 'react';

const TRANSITION_OUT_DELAY = 800;

/** Detect if the mouse is within `distance` of the bottom of the parent element,
 * with a delay when leaving the area
 */
export const useMouseArea = (
  distance: number
): [active: boolean, handler: (event: React.MouseEvent) => void] => {
  const [active, setActive] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>();

  // track the active value in a ref, so that we can use it in the handler
  // this means we can avoid multiple renders when transitioning from inactive to active
  const valueRef = useRef<boolean>(false);

  const handler = useCallback(
    (event: React.MouseEvent) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const newValue = rect.height - event.clientY < distance;

      if (newValue && timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }

      if (newValue && !valueRef.current) {
        setActive(true);
        valueRef.current = true;
      } else if (!newValue && valueRef.current) {
        if (!timer.current) {
          timer.current = setTimeout(() => {
            setActive(false);
            valueRef.current = false;
          }, TRANSITION_OUT_DELAY);
        }
      }
    },
    [distance]
  );

  return [active, handler];
};
